/**
 * StudentFormScreen — add / edit a student (PRD §3 #1).
 *
 * Add/edit the basics: first_name, age_years, disorder_category
 * (one of the 8 PRD categories), free-text notes, and the parent
 * contact fields (parent_first_name + parent_email). Parent
 * email is needed for the Slice 3 parent hand-off share-link
 * (we mint a token and email the link to the parent).
 *
 * PHI minimization is surfaced in the placeholder copy per
 * PRD §9 #2 (first name + age in years is the recommended
 * minimum).
 */
import { useEffect, useState } from 'react';
import type { ReactElement } from 'react';
import { supabase } from '../lib/supabase/client';
import {
  DISORDER_CATEGORIES,
  labelForDisorderCategory,
  type DisorderCategory,
} from '../lib/effectiveslp/domain';

interface Props {
  mode: 'new' | 'edit';
  studentId?: string;
  onSaved: (id: string) => void;
  onCancel: () => void;
}

interface FormState {
  firstName: string;
  ageYears: string;
  disorderCategory: DisorderCategory;
  notes: string;
  parentFirstName: string;
  parentEmail: string;
}

const INITIAL: FormState = {
  firstName: '',
  ageYears: '',
  disorderCategory: 'articulation',
  notes: '',
  parentFirstName: '',
  parentEmail: '',
};

export function StudentFormScreen({
  mode,
  studentId,
  onSaved,
  onCancel,
}: Props): ReactElement {
  const [form, setForm] = useState<FormState>(INITIAL);
  const [error, setError] = useState<string | null>(null);
  const [saving, setSaving] = useState(false);
  const [loaded, setLoaded] = useState(mode === 'new');

  useEffect(() => {
    if (mode !== 'edit' || !studentId) return;
    let cancelled = false;
    void (async () => {
      const { data, error: e } = await supabase
        .from('students')
        .select('first_name, age_years, disorder_category, notes, parent_first_name, parent_email')
        .eq('id', studentId)
        .maybeSingle();
      if (cancelled) return;
      if (e) {
        setError(e.message);
        setLoaded(true);
        return;
      }
      if (data) {
        const row = data as {
          first_name: string;
          age_years: number | null;
          disorder_category: string;
          notes: string | null;
          parent_first_name: string | null;
          parent_email: string | null;
        };
        setForm({
          firstName: row.first_name,
          ageYears: row.age_years != null ? String(row.age_years) : '',
          disorderCategory: (row.disorder_category as DisorderCategory) ?? 'articulation',
          notes: row.notes ?? '',
          parentFirstName: row.parent_first_name ?? '',
          parentEmail: row.parent_email ?? '',
        });
      }
      setLoaded(true);
    })();
    return () => {
      cancelled = true;
    };
  }, [mode, studentId]);

  function setField<K extends keyof FormState>(
    key: K,
    value: FormState[K],
  ): void {
    setForm((prev) => ({ ...prev, [key]: value }));
  }

  async function handleSubmit(e: React.FormEvent<HTMLFormElement>): Promise<void> {
    e.preventDefault();
    const trimmed = form.firstName.trim();
    if (!trimmed) {
      setError('First name is required.');
      return;
    }
    setSaving(true);
    setError(null);
    const ageNum = form.ageYears.trim() === '' ? null : parseInt(form.ageYears, 10);
    const payload = {
      first_name: trimmed,
      age_years: Number.isFinite(ageNum) ? ageNum : null,
      disorder_category: form.disorderCategory,
      notes: form.notes.trim() === '' ? null : form.notes.trim(),
      parent_first_name: form.parentFirstName.trim() === '' ? null : form.parentFirstName.trim(),
      parent_email: form.parentEmail.trim() === '' ? null : form.parentEmail.trim(),
    };
    try {
      if (mode === 'new') {
        const { data, error: iErr } = await supabase
          .from('students')
          .insert(payload)
          .select('id')
          .single();
        if (iErr) {
          setError(iErr.message);
          return;
        }
        onSaved((data as { id: string }).id);
      } else {
        const { error: uErr } = await supabase
          .from('students')
          .update(payload)
          .eq('id', studentId ?? '');
        if (uErr) {
          setError(uErr.message);
          return;
        }
        onSaved(studentId ?? '');
      }
    } finally {
      setSaving(false);
    }
  }

  if (!loaded) {
    return (
      <main className="screen">
        <p className="tagline">Loading…</p>
      </main>
    );
  }

  return (
    <main className="screen form-screen">
      <header className="screen-header">
        <button type="button" className="link-button" onClick={onCancel}>
          ← Cancel
        </button>
        <h1>{mode === 'new' ? 'Add student' : 'Edit student'}</h1>
      </header>

      <form onSubmit={(e) => void handleSubmit(e)}>
        <label>
          First name
          <input
            type="text"
            value={form.firstName}
            onChange={(e) => setField('firstName', e.target.value)}
            placeholder="First name only — keeps PHI minimal"
            required
          />
        </label>

        <label>
          Age (years)
          <input
            type="number"
            inputMode="numeric"
            min={0}
            max={21}
            value={form.ageYears}
            onChange={(e) => setField('ageYears', e.target.value)}
            placeholder="Age in years — no date of birth"
          />
        </label>

        <label>
          Disorder category
          <select
            value={form.disorderCategory}
            onChange={(e) =>
              setField('disorderCategory', e.target.value as DisorderCategory)
            }
          >
            {DISORDER_CATEGORIES.map((c) => (
              <option key={c} value={c}>
                {labelForDisorderCategory(c)}
              </option>
            ))}
          </select>
        </label>

        <label>
          Notes
          <textarea
            value={form.notes}
            onChange={(e) => setField('notes', e.target.value)}
            placeholder="Free-text notes (optional)"
          />
        </label>

        <fieldset className="form-fieldset">
          <legend>Parent contact</legend>
          <p className="tagline">
            Used for the parent hand-off share-link in Slice 3.
            Stored alongside the student record and visible only to you.
          </p>
          <label>
            Parent first name
            <input
              type="text"
              value={form.parentFirstName}
              onChange={(e) => setField('parentFirstName', e.target.value)}
              placeholder="Parent first name (optional)"
              autoComplete="off"
            />
          </label>
          <label>
            Parent email
            <input
              type="email"
              inputMode="email"
              autoComplete="off"
              value={form.parentEmail}
              onChange={(e) => setField('parentEmail', e.target.value)}
              placeholder="parent@example.com (optional)"
              aria-describedby="parent-email-hint"
            />
            <span id="parent-email-hint" className="tagline">
              Used by "Email parent" on the session plan page
            </span>
          </label>
        </fieldset>

        {error && <p className="error">{error}</p>}

        <div className="form-actions">
          <button type="submit" className="snap-button" disabled={saving}>
            {saving ? 'Saving…' : mode === 'new' ? 'Add student' : 'Save changes'}
          </button>
        </div>
      </form>
    </main>
  );
}
